<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Bank Reconciliation</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <form class="px-6 pb-5 pt-2" @submit.prevent>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Choose Bank</label>
          <select
            v-model="form.selectedBankId"
            :disabled="isLoadingBanks"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
          >
            <option value="">
              {{ isLoadingBanks ? 'Loading banks...' : bankOptions.length === 0 ? 'No banks available' : 'Choose Bank' }}
            </option>
            <option
              v-for="bank in bankOptions"
              :key="bank.id"
              :value="bank.id"
            >
              {{ bankLabel(bank) }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select Date</label>
          <input
            v-model="form.selectedDate"
            type="date"
            class="px-2.5 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Select type</label>
          <select
            v-model="form.transactionType"
            :disabled="!canSelectType"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
          >
            <option value="">Select type</option>
            <option
              v-for="option in transactionTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </form>

    <div class="px-2 pb-6 pt-2">
      <p
        v-if="!showReportContent"
        class="text-sm text-slate-500 text-center py-10 m-0 italic"
      >
        Select a bank, date, and transaction type to view bank reconciliation.
      </p>

      <div v-else class="w-full">
        <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0 px-4">
          {{ reportPeriodTitle }}
        </p>

        <div class="bank-recon-table-scroll relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)] px-2">
          <p
            v-if="isLoadingTransactions"
            class="text-sm text-slate-500 text-center py-10 m-0 italic"
          >
            Loading transactions...
          </p>
          <table v-else class="bank-recon-table border-collapse">
            <thead>
              <tr>
                <th>Status</th>
                <th>Source</th>
                <th>Reference</th>
                <th>Credit Deposit</th>
                <th>Cheque/Bank Debit</th>
                <th>Date</th>
                <th>Description</th>
                <th class="col-remark">Remark</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredRows"
                :key="row.id"
                class="hover:bg-slate-50 transition-colors border-b"
              >
                <td>
                  <input
                    :checked="row.reconciled"
                    type="checkbox"
                    class="bank-recon-checkbox"
                    :disabled="!canToggleCheckStatus(row) || row._updatingCheck"
                    :aria-label="`Reconciliation status for ${row.reference}`"
                    @change="onRowReconciledChange(row, $event)"
                  />
                </td>
                <td>{{ row.source }}</td>
                <td>
                  <button
                    type="button"
                    class="bank-recon-ref-link"
                    @click="openRowDetail(row)"
                  >
                    {{ row.referenceDisplay || row.reference }}
                  </button>
                </td>
                <td>{{ fmtReconDebit(row) }}</td>
                <td>{{ fmtReconCredit(row) }}</td>
               
                <td>{{ formatDisplayDate(row.date) }}</td>
                <td>{{ row.description }}</td>
                <td class="col-remark">
                  <input
                    v-model="row.remark"
                    type="text"
                    class="remark-table-field"
                    placeholder=""
                  />
                </td>
              </tr>
              <tr v-if="filteredRows.length === 0">
                <td colspan="8" class="text-center text-slate-400 italic py-5">
                  No transactions found for the selected type.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="bank-recon-summary px-4 pb-2">
          <div class="bank-recon-summary-grid">
            <div class="bank-recon-summary-col">
              <div class="bank-recon-collapsible">
                <button
                  type="button"
                  class="bank-recon-collapsible-trigger"
                  :aria-expanded="interestIncomeExpanded"
                  aria-controls="interest-income-panel"
                  @click="interestIncomeExpanded = !interestIncomeExpanded"
                >
                  <span class="bank-recon-collapsible-label">Interest Income</span>
                  <span class="bank-recon-collapsible-total">{{ fmtAmount(computedInterestIncomeTotal) || '0.00' }}</span>
                  <svg
                    class="bank-recon-collapsible-chevron"
                    :class="{ 'bank-recon-collapsible-chevron--open': interestIncomeExpanded }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div
                  v-show="interestIncomeExpanded"
                  id="interest-income-panel"
                  class="bank-recon-collapsible-panel"
                >
                  <div class="bank-recon-entry-list">
                    <div
                      v-for="(row, index) in interestIncomeRows"
                      :key="row.id"
                      class="bank-recon-entry-item"
                    >
                      <div class="bank-recon-entry-fields">
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Date</label>
                          <input
                            v-model="row.date"
                            type="date"
                            class="bank-recon-field-input"
                          />
                        </div>
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Amount</label>
                          <input
                            v-model="row.amount"
                            type="text"
                            class="bank-recon-field-input"
                          />
                        </div>
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Account</label>
                          <select
                            v-model="row.accountId"
                            :disabled="isLoadingChartAccounts"
                            class="bank-recon-field-select"
                          >
                            <option value="">
                              {{ isLoadingChartAccounts ? 'Loading accounts...' : 'Chart of Accounts' }}
                            </option>
                            <option
                              v-for="acc in enabledChartAccounts"
                              :key="`interest-row-${row.id}-${acc.id}`"
                              :value="String(acc.id)"
                            >
                              {{ formatAccountLabel(acc) }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <button
                        v-if="interestIncomeRows.length > 1"
                        type="button"
                        class="bank-recon-entry-remove"
                        aria-label="Remove interest income row"
                        @click="removeInterestIncomeRow(index)"
                      >
                        −
                      </button>
                    </div>

                    <button
                      type="button"
                      class="bank-recon-entry-add"
                      @click="addInterestIncomeRow"
                    >
                      + Add Interest Income
                    </button>
                  </div>
                </div>
              </div>

              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Imported Interest</label>
                <input
                  v-model="summaryForm.importedInterest"
                  type="text"
                  class="bank-recon-field-input"
                />
              </div>
            </div>

            <div class="bank-recon-summary-col">
              <div class="bank-recon-collapsible">
                <button
                  type="button"
                  class="bank-recon-collapsible-trigger"
                  :aria-expanded="serviceChargeExpanded"
                  aria-controls="service-charge-panel"
                  @click="serviceChargeExpanded = !serviceChargeExpanded"
                >
                  <span class="bank-recon-collapsible-label">Service Charge</span>
                  <span class="bank-recon-collapsible-total">{{ fmtAmount(computedServiceChargeTotal) || '0.00' }}</span>
                  <svg
                    class="bank-recon-collapsible-chevron"
                    :class="{ 'bank-recon-collapsible-chevron--open': serviceChargeExpanded }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div
                  v-show="serviceChargeExpanded"
                  id="service-charge-panel"
                  class="bank-recon-collapsible-panel"
                >
                  <div class="bank-recon-entry-list">
                    <div
                      v-for="(row, index) in serviceChargeRows"
                      :key="row.id"
                      class="bank-recon-entry-item"
                    >
                      <div class="bank-recon-entry-fields">
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Date</label>
                          <input
                            v-model="row.date"
                            type="date"
                            class="bank-recon-field-input"
                          />
                        </div>
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Amount</label>
                          <input
                            v-model="row.amount"
                            type="text"
                            class="bank-recon-field-input"
                          />
                        </div>
                        <div class="bank-recon-field-row">
                          <label class="bank-recon-field-label">Account</label>
                          <select
                            v-model="row.accountId"
                            :disabled="isLoadingChartAccounts"
                            class="bank-recon-field-select"
                          >
                            <option value="">
                              {{ isLoadingChartAccounts ? 'Loading accounts...' : 'Chart of Accounts' }}
                            </option>
                            <option
                              v-for="acc in enabledChartAccounts"
                              :key="`charge-row-${row.id}-${acc.id}`"
                              :value="String(acc.id)"
                            >
                              {{ formatAccountLabel(acc) }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <button
                        v-if="serviceChargeRows.length > 1"
                        type="button"
                        class="bank-recon-entry-remove"
                        aria-label="Remove service charge row"
                        @click="removeServiceChargeRow(index)"
                      >
                        −
                      </button>
                    </div>

                    <button
                      type="button"
                      class="bank-recon-entry-add"
                      @click="addServiceChargeRow"
                    >
                      + Add Service Charge
                    </button>
                  </div>
                </div>
              </div>

              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Imported Charge</label>
                <input
                  v-model="summaryForm.importedCharge"
                  type="text"
                  class="bank-recon-field-input"
                />
              </div>
            </div>

            <div class="bank-recon-summary-col">
              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Statement Ending Balance</label>
                <input
                  v-model="summaryForm.statementEndingBalance"
                  type="text"
                  class="bank-recon-field-input"
                />
              </div>
              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Outstanding Cheques</label>
                <input
                  :value="fmtAmount(computedOutstandingCheques) || '0.00'"
                  type="text"
                  class="bank-recon-field-input"
                  readonly
                />
              </div>
              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Deposits in Transit</label>
                <input
                  :value="fmtAmount(computedDepositsInTransit) || '0.00'"
                  type="text"
                  class="bank-recon-field-input"
                  readonly
                />
              </div>
              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">GL (System Balance)</label>
                <input
                  :value="fmtAmount(computedGlSystemBalance) || '0.00'"
                  type="text"
                  class="bank-recon-field-input"
                  readonly
                />
              </div>
              <div class="bank-recon-field-row">
                <label class="bank-recon-field-label">Unreconciled Difference</label>
                <input
                  :value="fmtAmount(computedReconciledDifference) || '0.00'"
                  type="text"
                  class="bank-recon-field-input"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-center px-4 pt-4 pb-2">
          <button
            type="button"
            :disabled="!canClickSave"
            class="min-w-[120px] px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            @click="openSaveReconciliationModal"
          >
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="depositModal.show"
        class="fixed inset-0 z-[10000] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm"
        @click.self="closeDepositModal"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-8 text-left relative overflow-hidden">
          <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
            <h2 class="text-lg font-bold m-0">
              Cash Income · {{ depositModal.postingDate ? formatDisplayDate(depositModal.postingDate) : '—' }}
            </h2>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
              @click="closeDepositModal"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-4">
            <p v-if="depositModal.loading" class="text-sm text-slate-500 text-center py-8 italic m-0">
              Loading saved income...
            </p>
            <div v-else class="relative overflow-auto custom-scrollbar border border-slate-200 rounded-lg">
              <table class="w-full today-cash-table min-w-max">
                <thead>
                  <tr>
                    <th class="col-sn">S/N</th>
                    <th class="col-income">Income</th>
                    <th class="num col-sys-hidden">Sales<br>(Sys) Taxable</th>
                    <th class="num">Sales<br>(Man) Taxable</th>
                    <th class="num total-col">Total<br>Taxable</th>
                    <th class="num col-sys-hidden">Sales<br>(Sys) Non-Taxable</th>
                    <th class="num">Sales<br>(Man) Non-Taxable</th>
                    <th class="num total-col">Total<br>Non-Taxable</th>
                    <th class="num col-sys-hidden">Sales (Sys)<br>Taxable SC</th>
                    <th class="num">Sales (Man)<br>Taxable SC</th>
                    <th class="num total-col">Total<br>Taxable SC</th>
                    <th class="num col-sys-hidden">Sales (Sys)<br>Non-Taxable SC</th>
                    <th class="num">Sales (Man)<br>Non-Taxable SC</th>
                    <th class="num total-col">Total<br>Non-Taxable SC</th>
                    <th class="num">Total<br>Before</th>
                    <th class="num col-sys-hidden">VAT<br>(Sys)</th>
                    <th class="num">VAT<br>(Man)</th>
                    <th class="num">Variation</th>
                    <th class="num total-col">Total<br>VAT</th>
                    <th class="num">Total<br>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(incomeRow, idx) in depositModal.rows"
                    :key="`dep-${idx}-${incomeRow.incomeCategoryId}`"
                    class="hover:bg-slate-50 transition-colors border-b"
                  >
                    <td class="col-sn">{{ idx + 1 }}</td>
                    <td
                      class="col-income"
                      :class="{ 'font-bold text-slate-700': isIncomeCategoryRow(incomeRow) }"
                    >{{ incomeRow.categoryName || incomeRow.incomeCategoryId }}</td>
                    <td class="num col-sys-hidden text-slate-600">{{ fmt(rowSalesSys(incomeRow, 'taxable')) }}</td>
                    <td class="num text-slate-600">{{ fmt(incomeRow.salesManTaxable) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(rowSalesTotal(incomeRow, 'taxable')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(rowSalesSys(incomeRow, 'taxable')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(rowSalesMan(incomeRow, 'taxable')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden text-slate-600">{{ fmt(rowSalesSys(incomeRow, 'nonTaxable')) }}</td>
                    <td class="num text-slate-600">{{ fmt(incomeRow.salesManNoTax) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(rowSalesTotal(incomeRow, 'nonTaxable')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(rowSalesSys(incomeRow, 'nonTaxable')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(rowSalesMan(incomeRow, 'nonTaxable')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden text-slate-600">{{ fmt(rowSalesSys(incomeRow, 'taxableSc')) }}</td>
                    <td class="num text-slate-600">{{ fmt(incomeRow.salesManTaxableSc) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(rowSalesTotal(incomeRow, 'taxableSc')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(rowSalesSys(incomeRow, 'taxableSc')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(rowSalesMan(incomeRow, 'taxableSc')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden text-slate-600">{{ fmt(rowSalesSys(incomeRow, 'nonTaxableSc')) }}</td>
                    <td class="num text-slate-600">{{ fmt(incomeRow.salesManNoTaxSc) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(rowSalesTotal(incomeRow, 'nonTaxableSc')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(rowSalesSys(incomeRow, 'nonTaxableSc')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(rowSalesMan(incomeRow, 'nonTaxableSc')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num">{{ fmt(rowTotalBefore(incomeRow, true)) }}</td>
                    <td class="num col-sys-hidden text-slate-600">{{ fmt(rowVatSys(incomeRow, true)) }}</td>
                    <td class="num text-slate-600">{{ fmt(rowVatMan(incomeRow)) }}</td>
                    <td class="num variation-cell">{{ fmt(rowVatManVariation(incomeRow)) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value vat-total-value">{{ fmt(rowVatTotal(incomeRow, true)) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(rowVatSys(incomeRow, true)) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(rowVatMan(incomeRow)) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num font-bold text-slate-700">{{ fmt(rowTotalSales(incomeRow, true)) }}</td>
                  </tr>
                  <tr v-if="depositModal.rows.length === 0">
                    <td :colspan="depositModalTableColspan" class="py-8 text-center text-slate-400 italic">No saved income rows.</td>
                  </tr>
                </tbody>
                <tfoot v-if="depositModal.rows.length > 0">
                  <tr class="h-3 border-none bg-transparent">
                    <td :colspan="depositModalTableColspan" class="border-none p-0"></td>
                  </tr>
                  <tr class="footer-total font-semibold bg-slate-100/80 border-t border-slate-200">
                    <td colspan="2">Total Sales</td>
                    <td class="num col-sys-hidden">{{ fmt(sumDepositCol('salesSysTaxable')) }}</td>
                    <td class="num">{{ fmt(sumDepositCol('salesManTaxable')) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(sumDepositTotalPair('salesSysTaxable', 'salesManTaxable')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable: <strong>{{ fmt(sumDepositCol('salesSysTaxable')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable: <strong>{{ fmt(sumDepositCol('salesManTaxable')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden">{{ fmt(sumDepositCol('salesSysNoTax')) }}</td>
                    <td class="num">{{ fmt(sumDepositCol('salesManNoTax')) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(sumDepositTotalPair('salesSysNoTax', 'salesManNoTax')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable: <strong>{{ fmt(sumDepositCol('salesSysNoTax')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable: <strong>{{ fmt(sumDepositCol('salesManNoTax')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden">{{ fmt(sumDepositCol('salesSysTaxableSc')) }}</td>
                    <td class="num">{{ fmt(sumDepositCol('salesManTaxableSc')) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(sumDepositTotalPair('salesSysTaxableSc', 'salesManTaxableSc')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Taxable SC: <strong>{{ fmt(sumDepositCol('salesSysTaxableSc')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Taxable SC: <strong>{{ fmt(sumDepositCol('salesManTaxableSc')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num col-sys-hidden">{{ fmt(sumDepositCol('salesSysNoTaxSc')) }}</td>
                    <td class="num">{{ fmt(sumDepositCol('salesManNoTaxSc')) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value">{{ fmt(sumDepositTotalPair('salesSysNoTaxSc', 'salesManNoTaxSc')) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> Sales (Sys) Non-Taxable SC: <strong>{{ fmt(sumDepositCol('salesSysNoTaxSc')) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> Sales (Man) Non-Taxable SC: <strong>{{ fmt(sumDepositCol('salesManNoTaxSc')) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num">{{ fmt(depositModal.rows.reduce((a, r) => a + rowTotalBefore(r, true), 0)) }}</td>
                    <td class="num col-sys-hidden">{{ fmt(sumDepositVatSysTotal()) }}</td>
                    <td class="num">{{ fmt(sumDepositVatManTotal()) }}</td>
                    <td class="num variation-cell">{{ fmt(sumDepositVatManVariationTotal()) }}</td>
                    <td class="num total-cell">
                      <div class="total-cell-wrap pinned-breakdown-wrap" @mouseenter="pinBreakdownDropdown" @mouseleave="resetBreakdownDropdown">
                        <span class="total-cell-value vat-total-value">{{ fmt(sumDepositVatCombinedTotal()) }}</span>
                        <div class="total-cell-dropdown">
                          <div class="total-cell-dropdown-title">Breakdown</div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">System</span> VAT (Sys): <strong>{{ fmt(sumDepositVatSysTotal()) }}</strong></div>
                          <div class="total-cell-dropdown-line"><span class="total-cell-tag">Manual</span> VAT (Man): <strong>{{ fmt(sumDepositVatManTotal()) }}</strong></div>
                        </div>
                      </div>
                    </td>
                    <td class="num text-slate-700 font-bold">{{ fmt(depositModal.rows.reduce((a, r) => a + rowTotalSales(r, true), 0)) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
            <button
              type="button"
              class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all"
              @click="closeDepositModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="withdrawalModal.show"
        class="fixed inset-0 z-[10000] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm"
        @click.self="closeWithdrawalModal"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-8 text-left relative overflow-hidden">
          <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
            <h2 class="text-lg font-bold m-0">
              Payment Purchase · {{ withdrawalModal.cpvNumber || '—' }}
            </h2>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
              @click="closeWithdrawalModal"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-4">
            <p v-if="withdrawalModal.loading" class="text-sm text-slate-500 text-center py-8 italic m-0">
              Loading payment purchase...
            </p>
            <template v-else>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div><span class="font-semibold text-slate-600">CPV No:</span> {{ withdrawalModal.detail?.cpvNumber || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Payment Type:</span> {{ withdrawalModal.detail?.paymentType || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Seller TIN:</span> {{ withdrawalModal.detail?.sellerTin || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Seller Name:</span> {{ withdrawalModal.detail?.sellerName || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Receipt No:</span> {{ withdrawalModal.detail?.receiptNumber || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Receipt Date:</span> {{ withdrawalModal.detail?.receiptDate ? formatDisplayDate(withdrawalModal.detail.receiptDate) : '—' }}</div>
                <div><span class="font-semibold text-slate-600">Cheque No:</span> {{ withdrawalModal.detail?.chequeNo || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Purpose:</span> {{ withdrawalModal.detail?.purposeOfPayment || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Source:</span> {{ withdrawalModal.detail?.sourceType || '—' }}</div>
                <div><span class="font-semibold text-slate-600">GRV/NS:</span> {{ withdrawalModal.detail?.grvNumber || withdrawalModal.detail?.nonStockableNumber || '—' }}</div>
                <div><span class="font-semibold text-slate-600">Date:</span> {{ formatDisplayDate(withdrawalModal.detail?.createdAt?.slice?.(0, 10) || withdrawalModal.detail?.createdAt) || '—' }}</div>
              </div>

              <div class="withdrawal-amount-summary mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-2 m-0">Amount breakdown</p>
                <div class="withdrawal-amount-grid text-sm">
                  <div class="withdrawal-amount-row">
                    <span class="withdrawal-amount-label">Base Amount:</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.baseAmount) }}</span>
                  </div>
                  <div class="withdrawal-amount-row">
                    <span class="withdrawal-amount-label">Taxable Amount:</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.taxableAmount) }}</span>
                  </div>
                  <div class="withdrawal-amount-row">
                    <span class="withdrawal-amount-label">Tax ({{ withdrawalModal.detail?.taxPct ?? 0 }}%):</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.taxAmount) }}</span>
                  </div>
                  <div class="withdrawal-amount-row withdrawal-amount-row--grand">
                    <span class="withdrawal-amount-label">Grand Total:</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.grandTotal) }}</span>
                  </div>
                  <div class="withdrawal-amount-row">
                    <span class="withdrawal-amount-label">Additional Amt:</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.additionalAmount) }}</span>
                  </div>
                  <template v-if="isCpvPaymentType(withdrawalModal.detail?.paymentType)">
                    <div class="withdrawal-amount-row">
                      <span class="withdrawal-amount-label">Service:</span>
                      <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.serviceAmount) }}</span>
                    </div>
                    <div class="withdrawal-amount-row">
                      <span class="withdrawal-amount-label">Goods:</span>
                      <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.goodsAmount) }}</span>
                    </div>
                    <div class="withdrawal-amount-row withdrawal-amount-row--grand">
                      <span class="withdrawal-amount-label">Withholding:</span>
                      <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.withholdingAmount) }}</span>
                    </div>
                  </template>
                  <div class="withdrawal-amount-row withdrawal-amount-row--payable">
                    <span class="withdrawal-amount-label">Amount in Figures:</span>
                    <span class="withdrawal-amount-value">{{ fmt(withdrawalModal.detail?.amountInFigures) }}</span>
                  </div>
                </div>
              </div>

              <div class="relative overflow-auto custom-scrollbar border border-slate-200 rounded-lg">
                <table class="w-full withdrawal-detail-table min-w-max">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Code</th>
                      <th>Item Name</th>
                      <th>Supplier</th>
                      <th>Category</th>
                      <th class="num">Qty</th>
                      <th class="num">Unit Price</th>
                      <th class="num">Total</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="line in withdrawalModal.lines" :key="line.id || line.lineNo">
                      <td>{{ line.lineNo }}</td>
                      <td>{{ line.itemCode || '—' }}</td>
                      <td>{{ line.itemName || '—' }}</td>
                      <td>{{ line.supplierName || '—' }}</td>
                      <td>{{ line.category || line.purchaseCategory || '—' }}</td>
                      <td class="num">{{ fmt(line.qty) }}</td>
                      <td class="num">{{ fmt(line.unitPrice) }}</td>
                      <td class="num">{{ fmt(line.totalPrice) }}</td>
                      <td>{{ line.remark || '—' }}</td>
                    </tr>
                    <tr v-if="withdrawalModal.lines.length === 0">
                      <td colspan="9" class="py-8 text-center text-slate-400 italic">No line items.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>

          <div class="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
            <button
              type="button"
              class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all"
              @click="closeWithdrawalModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="saveReconciliationModal.open"
        class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
        @click.self="closeSaveReconciliationModal"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all text-left relative overflow-hidden">
          <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
            <h3 class="text-base font-bold m-0">Confirm Save Bank Reconciliation</h3>
            <button
              type="button"
              class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
              aria-label="Close save modal"
              @click="closeSaveReconciliationModal"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="p-5 space-y-4">
            <div class="text-xs font-semibold text-slate-500">
              Please enter the code below to confirm that you want to save.
            </div>

            <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
              {{ saveReconciliationModal.code }}
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
              <input
                v-model.trim="saveReconciliationModal.input"
                type="text"
                maxlength="4"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
              >
            </div>

            <div v-if="saveReconciliationModal.error" class="text-xs text-red-600 font-semibold">
              {{ saveReconciliationModal.error }}
            </div>
          </div>

          <div class="px-5 py-4 flex items-center justify-end gap-2">
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
              @click="closeSaveReconciliationModal"
            >
              Cancel
            </button>
            <button
              type="button"
              :disabled="isSaving"
              class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
              @click="confirmSaveReconciliation"
            >
              {{ isSaving ? 'Saving...' : 'Confirm' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const store = useStore()

const transactionTypeOptions = [
  { value: 'all', label: 'All' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' }
]

const form = reactive({
  selectedBankId: '',
  selectedDate: '',
  transactionType: ''
})

const recentSavedReconciliation = ref(null)

const banks = ref([])
const reconciliationRows = ref([])
const isLoadingTransactions = ref(false)
const isLoadingBanks = ref(false)
const chartAccounts = ref([])
const isLoadingChartAccounts = ref(false)
const isSaving = ref(false)
const vatPct = ref(15)
const companyConstants = ref({ tax: 15, addition: 10 })
const depositModalTableColspan = 20
let reconciliationLoadSeq = 0

const saveReconciliationModal = ref({
  open: false,
  code: '',
  input: '',
  error: '',
})

const depositModal = reactive({
  show: false,
  loading: false,
  postingDate: '',
  rows: [],
})

const withdrawalModal = reactive({
  show: false,
  loading: false,
  cpvNumber: '',
  detail: null,
  lines: [],
})

const summaryForm = reactive({
  importedInterest: '',
  importedCharge: '',
  statementEndingBalance: '',
})

let summaryEntryRowSeq = 0

function createSummaryEntryRow() {
  summaryEntryRowSeq += 1
  return {
    id: `entry-${summaryEntryRowSeq}`,
    date: '',
    amount: '',
    accountId: '',
  }
}

const interestIncomeRows = ref([createSummaryEntryRow()])
const serviceChargeRows = ref([createSummaryEntryRow()])
const interestIncomeExpanded = ref(false)
const serviceChargeExpanded = ref(false)

const enabledChartAccounts = computed(() =>
  chartAccounts.value.filter((acc) => acc.enabled === true || acc.enabled === 1)
)

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddBankReconciliation = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Bank Reconciliation'), 'Add')
})

const canEditBankReconciliation = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Bank Reconciliation'), 'Edit')
})

const canSaveBankReconciliation = computed(() => {
  if (hasExistingReconciliation.value) return canEditBankReconciliation.value
  return canAddBankReconciliation.value
})

function canToggleCheckStatus(row) {
  if (!canSaveBankReconciliation.value) return false
  if (!row) return false
  if (row.type === 'deposit') return row.savedTotalIncomeId != null
  if (row.type === 'withdrawal') return row.paymentPurchaseId != null
  return false
}

async function onRowReconciledChange(row, event) {
  if (!canToggleCheckStatus(row)) {
    if (event?.target) event.target.checked = Boolean(row.reconciled)
    return
  }

  const checked = Boolean(event?.target?.checked)
  const previous = Boolean(row.reconciled)
  if (checked === previous) return

  const bankId = toIntOrNull(row.bankId ?? form.selectedBankId)
  if (!bankId) {
    if (event?.target) event.target.checked = previous
    showToast('Bank is required to update reconciliation status.', 'error')
    return
  }

  row.reconciled = checked
  row._updatingCheck = true

  try {
    const body =
      row.type === 'deposit'
        ? {
            type: 'deposit',
            bankId,
            savedTotalIncomeId: row.savedTotalIncomeId,
            isChecked: checked ? 1 : 0,
          }
        : {
            type: 'withdrawal',
            bankId,
            paymentPurchaseId: row.paymentPurchaseId,
            isChecked: checked ? 1 : 0,
          }

    const response = await apiFetch('/finance/bank-reconciliation/check-status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      row.reconciled = previous
      if (event?.target) event.target.checked = previous
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to update status.', 'error')
      return
    }
  } catch {
    row.reconciled = previous
    if (event?.target) event.target.checked = previous
    showToast('Failed to update status.', 'error')
  } finally {
    row._updatingCheck = false
  }
}

const bankOptions = computed(() => banks.value)

const canSelectType = computed(() =>
  Boolean(form.selectedBankId && form.selectedDate)
)

const effectiveUptoDate = computed(() => String(form.selectedDate || '').trim())

function addDaysToISODate(isoDate, days) {
  const s = String(isoDate || '').trim().slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return ''
  const d = new Date(`${s}T00:00:00.000Z`)
  if (Number.isNaN(d.getTime())) return ''
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

const lastSavedUptoDate = computed(() => {
  const header = recentSavedReconciliation.value
  if (!header) return ''
  const raw = header.uptoDate ?? header.upto_date
  const normalized = normalizeRowDate(raw)
  // Guard against malformed legacy API values like "Mon Jun 23" (no year).
  if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return ''
  return normalized
})

const effectiveFromDate = computed(() => {
  const lastUpto = lastSavedUptoDate.value
  const selected = effectiveUptoDate.value

  // Only use fromDate when continuing after the latest saved reconciliation.
  // Otherwise, backend should fetch everything up to uptoDate.
  if (lastUpto && selected && selected > lastUpto) {
    return addDaysToISODate(lastUpto, 1)
  }
  return ''
})

const hasExistingReconciliation = computed(() => {
  const lastUpto = lastSavedUptoDate.value
  const selected = effectiveUptoDate.value
  if (!lastUpto || !selected) return false
  return selected <= lastUpto
})

const showReportContent = computed(() =>
  Boolean(canSelectType.value && form.transactionType)
)

const canClickSave = computed(() => {
  if (isSaving.value || isLoadingTransactions.value) return false
  if (!showReportContent.value) return false
  if (filteredRows.value.length === 0) return false
  if (!canSaveBankReconciliation.value) return false
  return true
})

const filteredRows = computed(() => {
  let rows = reconciliationRows.value
  rows = applyEffectiveDateRangeFilter(rows)

  const selectedType = String(form.transactionType || '').toLowerCase()
  if (selectedType === 'deposit') {
    return rows.filter((row) => row.type === 'deposit')
  }
  if (selectedType === 'withdrawal') {
    return rows.filter((row) => row.type === 'withdrawal')
  }
  return rows
})

function rowMatchesEffectiveDateRange(row) {
  const rowDate = normalizeRowDate(row?.date)
  if (!rowDate) return false

  const from = effectiveFromDate.value
  const upto = effectiveUptoDate.value
  const lastUpto = lastSavedUptoDate.value

  if (from && rowDate < from) {
    const rowType = String(row?.type || '').toLowerCase()
    const isPriorUncheckedCarryForward =
      !row.reconciled &&
      lastUpto &&
      rowDate <= lastUpto &&
      (rowType === 'deposit' || rowType === 'withdrawal' || rowType === 'withdraw')
    if (!isPriorUncheckedCarryForward) return false
  }
  if (upto && rowDate > upto) return false
  return true
}

function applyEffectiveDateRangeFilter(rows) {
  if (!Array.isArray(rows)) return []
  return rows.filter(rowMatchesEffectiveDateRange)
}

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function toIntOrNull(v) {
  if (v === undefined || v === null || v === '') return null
  const num = Number(v)
  return Number.isFinite(num) ? parseInt(String(num), 10) : null
}

function fmt(x) {
  return n(x).toFixed(2)
}

function formatLocalISODate(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDisplayDate(isoDate) {
  const normalized = normalizeRowDate(isoDate)
  if (!normalized) return ''
  const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
  return normalized
}

const selectedBankName = computed(() => {
  const bankId = Number(form.selectedBankId)
  if (!Number.isFinite(bankId)) return ''
  const bank = banks.value.find((row) => Number(row.id) === bankId)
  return bank?.bankName || ''
})

const selectedTypeLabel = computed(() => {
  const option = transactionTypeOptions.find((item) => item.value === form.transactionType)
  return option?.label || '—'
})

const reportPeriodTitle = computed(() => {
  const bankName = selectedBankName.value || '—'
  const from = effectiveFromDate.value
    ? formatDisplayDate(effectiveFromDate.value)
    : 'Beginning'
  const upto = effectiveUptoDate.value ? formatDisplayDate(effectiveUptoDate.value) : '—'
  return `Bank reconciliation for ${bankName} (${from} - ${upto}) · ${selectedTypeLabel.value}`
})

function fmtAmount(value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (!Number.isFinite(num)) return ''
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtReconAmountCell(value) {
  if (value === null || value === undefined || value === '') return '—'
  return fmtAmount(value) || '—'
}

function fmtReconCredit(row) {
  const rowType = String(row?.type || '').toLowerCase()
  if (rowType === 'deposit') return '—'
  return fmtReconAmountCell(row?.chequeBankDebit)
}

function fmtReconDebit(row) {
  const rowType = String(row?.type || '').toLowerCase()
  if (rowType === 'withdrawal' || rowType === 'withdraw') return '—'
  return fmtReconAmountCell(row?.creditDeposit)
}

function parseSummaryAmount(value) {
  if (value === null || value === undefined || value === '') return 0
  const num = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(num) ? num : 0
}

function sumSummaryEntryRows(rows) {
  if (!Array.isArray(rows)) return 0
  const total = rows.reduce((sum, row) => sum + parseSummaryAmount(row?.amount), 0)
  return Math.round(total * 100) / 100
}

function buildSummaryEntryItemsForSave(rows) {
  if (!Array.isArray(rows)) return []
  return rows.map((row) => ({
    date: row?.date || '',
    amount: parseSummaryAmount(row?.amount),
    accountId: row?.accountId ? Number(row.accountId) : null,
  }))
}

function firstSummaryEntryRow(rows) {
  return Array.isArray(rows) && rows.length ? rows[0] : null
}

function addInterestIncomeRow() {
  interestIncomeRows.value.push(createSummaryEntryRow())
}

function removeInterestIncomeRow(index) {
  if (interestIncomeRows.value.length <= 1) return
  interestIncomeRows.value.splice(index, 1)
}

function addServiceChargeRow() {
  serviceChargeRows.value.push(createSummaryEntryRow())
}

function removeServiceChargeRow(index) {
  if (serviceChargeRows.value.length <= 1) return
  serviceChargeRows.value.splice(index, 1)
}

const computedInterestIncomeTotal = computed(() => sumSummaryEntryRows(interestIncomeRows.value))

const computedServiceChargeTotal = computed(() => sumSummaryEntryRows(serviceChargeRows.value))

const computedOutstandingCheques = computed(() => {
  let total = 0
  for (const row of filteredRows.value) {
    if (row.reconciled) continue
    const type = String(row?.type || '').toLowerCase()
    if (type === 'withdrawal' || type === 'withdraw') {
      total += n(row.chequeBankDebit)
    }
  }
  return Math.round(total * 100) / 100
})

const computedDepositsInTransit = computed(() => {
  let total = 0
  for (const row of filteredRows.value) {
    if (row.reconciled) continue
    const type = String(row?.type || '').toLowerCase()
    if (type === 'deposit') {
      total += n(row.creditDeposit)
    }
  }
  return Math.round(total * 100) / 100
})

const computedGlSystemBalance = computed(() => {
  const deposits = computedDepositsInTransit.value
  const cheques = computedOutstandingCheques.value
  const interest = computedInterestIncomeTotal.value
  const charge = computedServiceChargeTotal.value
  return Math.round((deposits - cheques + interest - charge) * 100) / 100
})

const computedReconciledDifference = computed(() => {
  const gl = computedGlSystemBalance.value
  const statement = parseSummaryAmount(summaryForm.statementEndingBalance)
  return Math.round((gl - statement) * 100) / 100
})

async function buildIncomeNameMap() {
  const user = store.getters.currentUser || store.state.user || {}
  const companyId = user?.companyId || user?.company_id || null
  const branchId = user?.branchId || user?.branch_id || null
  const map = new Map()

  try {
    const catRes = await apiFetch('/finance/income-category')
    if (catRes.ok) {
      const cats = await catRes.json().catch(() => [])
      for (const cat of Array.isArray(cats) ? cats : []) {
        if (cat?.id == null) continue
        const name = String(cat?.name || '').trim()
        if (name) map.set(String(cat.id), name)
      }
    }

    if (companyId || branchId) {
      let deptEndpoint = `/departments/company/${companyId}`
      if (branchId) deptEndpoint = `/departments/branch/${branchId}`
      const deptRes = await apiFetch(deptEndpoint)
      if (deptRes.ok) {
        const depts = await deptRes.json().catch(() => [])
        for (const dep of Array.isArray(depts) ? depts : []) {
          if (dep?.id == null) continue
          const name = String(dep?.name || '').trim()
          if (name) map.set(`department-${String(dep.id).trim()}`, name)
        }
      }
    }
  } catch {
    // Keep modal usable even if lookup fails.
  }

  return map
}

function normalizeIncomeRow(row, nameMap) {
  if (!row || typeof row !== 'object') return null
  const incomeCategoryId = row.incomeCategoryId
  const id = incomeCategoryId != null ? String(incomeCategoryId) : ''
  const categoryName =
    String(row.categoryName || '').trim() ||
    (nameMap instanceof Map ? nameMap.get(id) : '') ||
    ''
  return {
    incomeCategoryId,
    categoryType: row.categoryType || (id.startsWith('department-') ? 'department' : 'income-category'),
    categoryName,
    salesSysTaxable: n(row.salesSysTaxable),
    salesManTaxable: n(row.salesManTaxable ?? row.salesTaxable),
    salesSysNoTax: n(row.salesSysNoTax),
    salesManNoTax: n(row.salesManNoTax ?? row.salesNoTax),
    salesSysTaxableSc: n(row.salesSysTaxableSc ?? row.salesTaxableSc),
    salesManTaxableSc: n(row.salesManTaxableSc),
    salesSysNoTaxSc: n(row.salesSysNoTaxSc ?? row.salesNoTaxSc),
    salesManNoTaxSc: n(row.salesManNoTaxSc),
    vatMan: n(row.vatMan ?? row.vat_man),
    totalBefore: row.totalBefore != null || row.total_before != null
      ? n(row.totalBefore ?? row.total_before)
      : null,
    vatSys: row.vatSys != null || row.vat_sys != null
      ? n(row.vatSys ?? row.vat_sys)
      : null,
    totalSales: row.totalSales != null || row.total_sales != null
      ? n(row.totalSales ?? row.total_sales)
      : null,
  }
}

function isDepartmentRow(row) {
  const id = row?.incomeCategoryId
  return typeof id === 'string' && id.startsWith('department-')
}

function isIncomeCategoryRow(row) {
  if (row?.categoryType != null) return row.categoryType === 'income-category'
  return !isDepartmentRow(row)
}

const SALES_SYS_FIELDS = {
  taxable: 'salesSysTaxable',
  nonTaxable: 'salesSysNoTax',
  taxableSc: 'salesSysTaxableSc',
  nonTaxableSc: 'salesSysNoTaxSc',
}

const SALES_MAN_FIELDS = {
  taxable: 'salesManTaxable',
  nonTaxable: 'salesManNoTax',
  taxableSc: 'salesManTaxableSc',
  nonTaxableSc: 'salesManNoTaxSc',
}

function manAdditionRate() {
  return Math.max(0, n(companyConstants.value.addition)) / 100
}

function manTaxRate() {
  return Math.max(0, n(companyConstants.value.tax)) / 100
}

function calcManTaxableSc(taxable) {
  return Math.round(n(taxable) * manAdditionRate() * 100) / 100
}

function calcManVat(taxable, taxableSc) {
  const base = n(taxable) + n(taxableSc)
  return Math.round(base * manTaxRate() * 100) / 100
}

function rowSalesSys(row, field) {
  return n(row[SALES_SYS_FIELDS[field]])
}

function rowSalesMan(row, field) {
  return n(row[SALES_MAN_FIELDS[field]])
}

function rowSalesTotal(row, field) {
  return rowSalesSys(row, field) + rowSalesMan(row, field)
}

function rowTotalBefore(row, useStored = false) {
  if (useStored && row.totalBefore != null && row.totalBefore !== '') {
    return Math.round(n(row.totalBefore) * 100) / 100
  }
  return n(row.salesSysTaxable) + n(row.salesManTaxable) +
         n(row.salesSysNoTax) + n(row.salesManNoTax) +
         n(row.salesSysTaxableSc) + n(row.salesManTaxableSc) +
         n(row.salesSysNoTaxSc) + n(row.salesManNoTaxSc)
}

function rowVatSys(row, useStored = false) {
  if (useStored && row.vatSys != null && row.vatSys !== '') {
    return Math.round(n(row.vatSys) * 100) / 100
  }
  const base = n(row.salesSysTaxable) + n(row.salesSysTaxableSc)
  return Math.round(base * (n(vatPct.value) / 100) * 100) / 100
}

function rowVatMan(row) {
  return Math.round(n(row.vatMan) * 100) / 100
}

function rowVatManCalculated(row) {
  const taxable = n(row.salesManTaxable)
  const taxableScFromConstants = calcManTaxableSc(taxable)
  return calcManVat(taxable, taxableScFromConstants)
}

function rowVatManVariation(row) {
  return Math.round((rowVatManCalculated(row) - rowVatMan(row)) * 100) / 100
}

function rowVatTotal(row, useStored = false) {
  return rowVatSys(row, useStored) + rowVatMan(row)
}

function rowTotalSales(row, useStored = false) {
  if (useStored && row.totalSales != null && row.totalSales !== '') {
    return Math.round(n(row.totalSales) * 100) / 100
  }
  return Math.round((rowTotalBefore(row, useStored) + rowVatSys(row, useStored) + rowVatMan(row)) * 100) / 100
}

function sumDepositCol(prop) {
  return depositModal.rows.reduce((a, r) => a + n(r[prop]), 0)
}

function sumDepositTotalPair(sysProp, manProp) {
  return sumDepositCol(sysProp) + sumDepositCol(manProp)
}

function sumDepositVatSysTotal() {
  return depositModal.rows.reduce((a, r) => a + rowVatSys(r, true), 0)
}

function sumDepositVatManTotal() {
  return depositModal.rows.reduce((a, r) => a + rowVatMan(r), 0)
}

function sumDepositVatCombinedTotal() {
  return sumDepositVatSysTotal() + sumDepositVatManTotal()
}

function sumDepositVatManVariationTotal() {
  return depositModal.rows.reduce((a, r) => a + rowVatManVariation(r), 0)
}

function pinBreakdownDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.total-cell-dropdown')
  if (!dd) return
  const rect = wrap.getBoundingClientRect()
  const gap = 4
  dd.style.visibility = 'hidden'
  dd.style.display = 'block'
  dd.style.position = 'fixed'
  const h = dd.offsetHeight || 72
  const spaceBelow = window.innerHeight - rect.bottom - gap
  dd.style.left = `${rect.left + rect.width / 2}px`
  dd.style.transform = 'translateX(-50%)'
  dd.style.zIndex = '10001'
  dd.style.visibility = 'visible'
  if (spaceBelow < h) {
    dd.style.top = `${Math.max(gap, rect.top - h - gap)}px`
  } else {
    dd.style.top = `${rect.bottom + gap}px`
  }
}

function resetBreakdownDropdown(evt) {
  const wrap = evt.currentTarget
  const dd = wrap?.querySelector('.total-cell-dropdown')
  if (!dd) return
  dd.style.position = ''
  dd.style.left = ''
  dd.style.top = ''
  dd.style.transform = ''
  dd.style.zIndex = ''
  dd.style.visibility = ''
  dd.style.display = ''
}

function formatAccountLabel(acc) {
  const code = acc?.code ? String(acc.code) : ''
  const name = acc?.accountName ? String(acc.accountName) : ''
  return code && name ? `${code} - ${name} - ${acc.accountType}` : name || code || 'Account'
}

async function loadChartAccounts() {
  isLoadingChartAccounts.value = true
  try {
    const response = await apiFetch('/finance/chart-of-account', { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      chartAccounts.value = []
      showToast(data?.message || 'Failed to load chart of accounts.', 'error')
      return
    }
    const parsed = await response.json().catch(() => [])
    const list = Array.isArray(parsed) ? parsed : []
    list.sort((a, b) => String(a.accountName || '').localeCompare(String(b.accountName || '')))
    chartAccounts.value = list
  } catch {
    chartAccounts.value = []
    showToast('Failed to load chart of accounts.', 'error')
  } finally {
    isLoadingChartAccounts.value = false
  }
}

function bankLabel(bank) {
  const name = String(bank?.bankName || '').trim() || `Bank ${bank?.id}`
  if (bank?.active === false) {
    return `${name} (Inactive)`
  }
  return name
}

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveReconciliationModal() {
  if (!canClickSave.value) return
  saveReconciliationModal.value.open = true
  saveReconciliationModal.value.code = generateVerificationCode()
  saveReconciliationModal.value.input = ''
  saveReconciliationModal.value.error = ''
}

function closeSaveReconciliationModal() {
  if (isSaving.value) return
  saveReconciliationModal.value.open = false
}

function resetSummaryForm() {
  summaryForm.importedInterest = ''
  summaryForm.importedCharge = ''
  summaryForm.statementEndingBalance = ''
  interestIncomeRows.value = [createSummaryEntryRow()]
  serviceChargeRows.value = [createSummaryEntryRow()]
}

function buildReconciliationQueryParams() {
  const params = new URLSearchParams({
    bankId: String(form.selectedBankId),
    uptoDate: effectiveUptoDate.value,
    transactionType: form.transactionType,
  })
  if (effectiveFromDate.value) {
    params.set('fromDate', effectiveFromDate.value)
    if (lastSavedUptoDate.value) {
      params.set('includeUncheckedUptoDate', lastSavedUptoDate.value)
    }
  }
  return params
}

function resolveSaveFromDate() {
  const lastUpto = lastSavedUptoDate.value
  const selected = effectiveUptoDate.value

  if (lastUpto && selected && selected > lastUpto) {
    return addDaysToISODate(lastUpto, 1)
  }

  if (effectiveFromDate.value) return effectiveFromDate.value

  const dates = filteredRows.value
    .map((row) => normalizeRowDate(row.date))
    .filter(Boolean)
    .sort()
  if (dates.length) return dates[0]
  return effectiveUptoDate.value
}

function buildSavePayload() {
  const firstInterestRow = firstSummaryEntryRow(interestIncomeRows.value)
  const firstServiceChargeRow = firstSummaryEntryRow(serviceChargeRows.value)
  const interestIncomeItems = buildSummaryEntryItemsForSave(interestIncomeRows.value)
  const serviceChargeItems = buildSummaryEntryItemsForSave(serviceChargeRows.value)
  const interestIncomeTotal = computedInterestIncomeTotal.value
  const serviceChargeTotal = computedServiceChargeTotal.value

  return {
    bankId: Number(form.selectedBankId),
    fromDate: resolveSaveFromDate(),
    uptoDate: effectiveUptoDate.value,
    transactionType: form.transactionType,
    summary: {
      interestIncome: interestIncomeTotal,
      interestIncomeDate: firstInterestRow?.date || '',
      interestIncomeAccountId: firstInterestRow?.accountId || '',
      interestIncomeItems,
      importedInterest: summaryForm.importedInterest,
      serviceCharge: serviceChargeTotal,
      serviceChargeDate: firstServiceChargeRow?.date || '',
      serviceChargeAccountId: firstServiceChargeRow?.accountId || '',
      serviceChargeItems,
      importedCharge: summaryForm.importedCharge,
      statementEndingBalance: summaryForm.statementEndingBalance,
      outstandingCheques: fmtAmount(computedOutstandingCheques.value) || '0.00',
      depositsInTransit: fmtAmount(computedDepositsInTransit.value) || '0.00',
      glSystemBalance: fmtAmount(computedGlSystemBalance.value) || '0.00',
      reconciledDifference: fmtAmount(computedReconciledDifference.value) || '0.00',
    },
    rows: filteredRows.value.map((row, index) => ({
      lineNo: index + 1,
      type: row.type,
      bankId: row.bankId ?? form.selectedBankId ?? null,
      reconciled: Boolean(row.reconciled),
      source: row.source || 'System',
      reference: row.reference || '',
      creditDeposit: row.creditDeposit,
      chequeBankDebit: row.chequeBankDebit,
      date: row.date,
      description: row.description || '',
      remark: row.remark || '',
      savedTotalIncomeId: row.savedTotalIncomeId,
      paymentPurchaseId: row.paymentPurchaseId,
    })),
  }
}

async function persistSaveReconciliation() {
  if (!canClickSave.value) return false
  isSaving.value = true
  try {
    const response = await apiFetch('/finance/bank-reconciliation/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildSavePayload()),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      showToast(data?.message || 'Failed to save bank reconciliation.', 'error')
      return false
    }
    showToast('Bank reconciliation saved successfully.', 'success')
    await loadRecentSavedReconciliation(form.selectedBankId)
    return true
  } catch {
    showToast('Failed to save bank reconciliation.', 'error')
    return false
  } finally {
    isSaving.value = false
  }
}

async function confirmSaveReconciliation() {
  const expected = String(saveReconciliationModal.value.code || '').trim()
  const entered = String(saveReconciliationModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveReconciliationModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveReconciliationModal.value.error = ''
  const ok = await persistSaveReconciliation()
  if (ok) {
    saveReconciliationModal.value.open = false
  }
}

function mapTransactionRow(row, index) {
  const rawDate = row.date || row.reference || ''
  const postingDate = normalizeRowDate(rawDate)
  const rowType = String(row.type || '').toLowerCase()
  return {
    id: row.id || `${rowType || 'row'}-${index}`,
    type: rowType === 'withdraw' ? 'withdrawal' : rowType,
    bankId: row.bankId ?? row.bank_id ?? form.selectedBankId ?? null,
    savedTotalIncomeId: row.savedTotalIncomeId ?? row.saved_total_income_id ?? null,
    paymentPurchaseId: row.paymentPurchaseId ?? row.payment_purchase_id ?? null,
    reconciled: Boolean(row.reconciled),
    source: row.source || 'System',
    reference: row.reference || postingDate,
    referenceDisplay: rowType === 'deposit'
      ? formatDisplayDate(postingDate)
      : (row.reference || ''),
    creditDeposit: row.creditDeposit ?? row.credit_deposit ?? null,
    chequeBankDebit: row.chequeBankDebit ?? row.cheque_bank_debit ?? null,
    date: postingDate,
    description: row.description || '',
    remark: row.remark || '',
  }
}

function normalizeRowDate(value) {
  if (value == null || value === '') return ''
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return ''
    return formatLocalISODate(value)
  }
  const s = String(value).trim()
  // Date-only strings have no timezone — use as-is.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) return formatLocalISODate(parsed)
  return s.slice(0, 10)
}

async function loadRecentSavedReconciliation(bankId) {
  if (!bankId) {
    recentSavedReconciliation.value = null
    return
  }
  try {
    const res = await apiFetch(
      `/finance/bank-reconciliation/recent?bankId=${encodeURIComponent(bankId)}`,
      { method: 'GET' }
    )
    if (!res.ok) {
      recentSavedReconciliation.value = null
      return
    }
    const data = await res.json().catch(() => ({ hasData: false }))
    recentSavedReconciliation.value = data?.hasData ? data.header : null
  } catch {
    recentSavedReconciliation.value = null
  }
}

async function loadReconciliationData() {
  if (!showReportContent.value) {
    reconciliationRows.value = []
    return
  }

  const loadSeq = ++reconciliationLoadSeq
  isLoadingTransactions.value = true
  try {
    await loadRecentSavedReconciliation(form.selectedBankId)

    const fromDate = effectiveFromDate.value
    const params = buildReconciliationQueryParams()
    if (fromDate) {
      params.set('fromDate', fromDate)
    }

    const response = await apiFetch(`/finance/bank-reconciliation/transactions?${params}`, { method: 'GET' })
    if (loadSeq !== reconciliationLoadSeq) return

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      reconciliationRows.value = []
      resetSummaryForm()
      showToast(data?.message || 'Failed to load bank reconciliation transactions.', 'error')
      return
    }
    const data = await response.json().catch(() => [])
    const list = Array.isArray(data) ? data : []
    reconciliationRows.value = applyEffectiveDateRangeFilter(list.map(mapTransactionRow))
    resetSummaryForm()
  } catch {
    if (loadSeq !== reconciliationLoadSeq) return
    reconciliationRows.value = []
    resetSummaryForm()
    showToast('Failed to load bank reconciliation transactions.', 'error')
  } finally {
    if (loadSeq === reconciliationLoadSeq) {
      isLoadingTransactions.value = false
    }
  }
}

async function loadVatPctFromApi() {
  try {
    const res = await apiFetch('/finance/income-tax')
    if (!res.ok) {
      vatPct.value = 15
      return
    }
    const parsed = await res.json().catch(() => [])
    if (!Array.isArray(parsed)) {
      vatPct.value = 15
      return
    }
    const row = parsed.find((r) => r?.enabled !== false && r?.tax != null && Number(r.tax) >= 0)
    vatPct.value = row ? Number(row.tax) || 15 : 15
  } catch {
    vatPct.value = 15
  }
}

async function loadCompanyConstants() {
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    companyConstants.value = {
      tax: Number(data?.tax ?? 15),
      addition: Number(data?.addition ?? 10),
    }
  } catch {
    // keep defaults
  }
}

async function openRowDetail(row) {
  if (!row) return
  if (row.type === 'deposit' && row.savedTotalIncomeId) {
    await openDepositDetail(row.savedTotalIncomeId, row.date)
    return
  }
  if (row.type === 'withdrawal' && row.paymentPurchaseId) {
    await openWithdrawalDetail(row.paymentPurchaseId, row.reference)
  }
}

async function openDepositDetail(savedTotalIncomeId, postingDate) {
  depositModal.show = true
  depositModal.loading = true
  depositModal.postingDate = postingDate || ''
  depositModal.rows = []
  try {
    const res = await apiFetch(`/finance/bank-reconciliation/deposit/${savedTotalIncomeId}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load saved income detail.', 'error')
      closeDepositModal()
      return
    }
    const data = await res.json().catch(() => ({}))
    const rawRows = Array.isArray(data?.rows) ? data.rows : []
    const nameMap = await buildIncomeNameMap()
    depositModal.postingDate = normalizeRowDate(
      data?.postingDate || data?.footer?.postingDate || postingDate || ''
    )
    depositModal.rows = rawRows
      .map((row) => normalizeIncomeRow(row, nameMap))
      .filter(Boolean)
      .sort((a, b) => String(a.categoryName).localeCompare(String(b.categoryName)))
  } catch {
    showToast('Failed to load saved income detail.', 'error')
    closeDepositModal()
  } finally {
    depositModal.loading = false
  }
}

function closeDepositModal() {
  depositModal.show = false
  depositModal.loading = false
  depositModal.postingDate = ''
  depositModal.rows = []
}

function isCpvPaymentType(paymentType) {
  return String(paymentType || '').trim() === 'C.P.V'
}

function normalizeWithdrawalDetail(data) {
  if (!data || typeof data !== 'object') return null
  return {
    ...data,
    cpvNumber: data.cpvNumber ?? data.cpv_number ?? '',
    paymentType: data.paymentType ?? data.payment_type ?? '',
    sellerTin: data.sellerTin ?? data.seller_tin ?? '',
    sellerName: data.sellerName ?? data.seller_name ?? '',
    receiptNumber: data.receiptNumber ?? data.receipt_number ?? '',
    receiptDate: data.receiptDate ?? data.receipt_date ?? '',
    chequeNo: data.chequeNo ?? data.cheque_no ?? '',
    purposeOfPayment: data.purposeOfPayment ?? data.purpose_of_payment ?? '',
    sourceType: data.sourceType ?? data.source_type ?? '',
    grvNumber: data.grvNumber ?? data.grv_number ?? '',
    nonStockableNumber: data.nonStockableNumber ?? data.non_stockable_number ?? '',
    baseAmount: n(data.baseAmount ?? data.base_amount),
    taxableAmount: n(data.taxableAmount ?? data.taxable_amount),
    taxPct: n(data.taxPct ?? data.tax_pct),
    taxAmount: n(data.taxAmount ?? data.tax_amount),
    grandTotal: n(data.grandTotal ?? data.grand_total),
    additionalAmount: n(data.additionalAmount ?? data.additional_amount),
    serviceAmount: n(data.serviceAmount ?? data.service_amount),
    goodsAmount: n(data.goodsAmount ?? data.goods_amount),
    withholdingAmount: n(data.withholdingAmount ?? data.withholding_amount),
    amountInFigures: n(data.amountInFigures ?? data.amount_in_figures),
    createdAt: data.createdAt ?? data.created_at ?? '',
  }
}

async function openWithdrawalDetail(paymentPurchaseId, cpvNumber) {
  withdrawalModal.show = true
  withdrawalModal.loading = true
  withdrawalModal.cpvNumber = cpvNumber || ''
  withdrawalModal.detail = null
  withdrawalModal.lines = []
  try {
    const res = await apiFetch(`/finance/bank-reconciliation/withdrawal/${paymentPurchaseId}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load payment purchase detail.', 'error')
      closeWithdrawalModal()
      return
    }
    const data = await res.json().catch(() => null)
    const detail = normalizeWithdrawalDetail(data)
    withdrawalModal.detail = detail
    withdrawalModal.cpvNumber = detail?.cpvNumber || cpvNumber || ''
    withdrawalModal.lines = Array.isArray(detail?.lines) ? detail.lines : []
  } catch {
    showToast('Failed to load payment purchase detail.', 'error')
    closeWithdrawalModal()
  } finally {
    withdrawalModal.loading = false
  }
}

function closeWithdrawalModal() {
  withdrawalModal.show = false
  withdrawalModal.loading = false
  withdrawalModal.cpvNumber = ''
  withdrawalModal.detail = null
  withdrawalModal.lines = []
}

watch(
  () => form.selectedBankId,
  async (bankId) => {
    form.transactionType = ''
    reconciliationRows.value = []
    if (bankId) {
      await loadRecentSavedReconciliation(bankId)
    } else {
      recentSavedReconciliation.value = null
    }
  }
)

watch(
  () => [form.selectedBankId, form.selectedDate, form.transactionType, effectiveFromDate.value],
  () => {
    if (!canSelectType.value) {
      form.transactionType = ''
      reconciliationRows.value = []
    }
    if (showReportContent.value) {
      loadReconciliationData()
      return
    }
    reconciliationRows.value = []
  }
)

async function loadBanks() {
  isLoadingBanks.value = true
  try {
    const response = await apiFetch('/finance/bank-register', { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      banks.value = []
      showToast(data?.message || 'Failed to load banks.', 'error')
      return
    }
    const data = await response.json().catch(() => [])
    banks.value = Array.isArray(data) ? data : []
  } catch {
    banks.value = []
    showToast('Failed to load banks.', 'error')
  } finally {
    isLoadingBanks.value = false
  }
}

onMounted(() => {
  loadBanks()
  loadChartAccounts()
  loadVatPctFromApi()
  loadCompanyConstants()
})
</script>

<style scoped>
.bank-recon-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  color: #1e293b;
  border-collapse: collapse;
  table-layout: auto;
  width: max-content;
  min-width: 100%;
}

.bank-recon-table th,
.bank-recon-table td {
  padding: 3px 8px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
  font-size: 0.68rem;
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
  width: max-content;
  max-width: none;
}

.bank-recon-table thead th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  position: sticky;
  top: 0;
  z-index: 10;
}

.bank-recon-table tbody tr:hover {
  background: #f8fafc;
}

.bank-recon-table th:nth-child(7),
.bank-recon-table td:nth-child(7) {
  white-space: normal;
  word-break: break-word;
  min-width: 6rem;
  max-width: 18rem;
}

.bank-recon-table td.col-remark {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
  width: 4.75rem;
  min-width: 4.75rem;
}

.bank-recon-table th.col-remark {
  white-space: nowrap;
  width: 4.75rem;
  min-width: 4.75rem;
}

.remark-table-field {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 1px 3px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.66rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.remark-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

.bank-recon-checkbox {
  width: 0.85rem;
  height: 0.85rem;
  margin: 0;
  cursor: pointer;
  accent-color: #0d4169;
  vertical-align: middle;
}

.bank-recon-summary-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem 1.5rem;
}

.bank-recon-summary-col {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.bank-recon-field-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
}

.bank-recon-field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  line-height: 1.2;
}

.bank-recon-field-input,
.bank-recon-field-select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.375rem 0.625rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  outline: none;
  font-size: 0.875rem;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.15s;
}

.bank-recon-field-input:focus,
.bank-recon-field-select:focus {
  border-color: #0d4169;
}

.bank-recon-field-input:read-only {
  background: #f8fafc;
  color: #475569;
  cursor: default;
}

.bank-recon-field-select {
  cursor: pointer;
}

.bank-recon-field-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f8fafc;
}

.bank-recon-entry-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.bank-recon-entry-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: #f8fafc;
}

.bank-recon-entry-fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.bank-recon-collapsible {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.bank-recon-collapsible-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.375rem 0.625rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s;
}

.bank-recon-collapsible-trigger:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

.bank-recon-collapsible-trigger:focus {
  outline: none;
  border-color: #0d4169;
}

.bank-recon-collapsible-label {
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e293b;
}

.bank-recon-collapsible-total {
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-weight: 600;
}

.bank-recon-collapsible-chevron {
  flex-shrink: 0;
  width: 1rem;
  height: 1rem;
  color: #64748b;
  transition: transform 0.15s;
}

.bank-recon-collapsible-chevron--open {
  transform: rotate(180deg);
}

.bank-recon-collapsible-panel {
  margin-top: 0.5rem;
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: #f8fafc;
}

.bank-recon-entry-remove {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  margin-top: 0.25rem;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  background: #fff;
  color: #dc2626;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.bank-recon-entry-remove:hover {
  background: #fef2f2;
  border-color: #f87171;
}

.bank-recon-entry-add {
  align-self: flex-start;
  padding: 0.375rem 0.75rem;
  border: 1px dashed #94a3b8;
  border-radius: 0.375rem;
  background: #fff;
  color: #0d4169;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.bank-recon-entry-add:hover {
  background: #f8fafc;
  border-color: #0d4169;
}

@media (min-width: 1024px) {
  .bank-recon-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.bank-recon-ref-link {
  display: inline;
  background: none;
  border: none;
  padding: 0;
  color: #2563eb;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  transition: color 0.15s;
}

.bank-recon-ref-link:hover {
  color: #0d4169;
}

.today-cash-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: separate;
  border-spacing: 0;
  width: auto;
  min-width: 100%;
}

.today-cash-table th,
.today-cash-table td {
  padding: 4px 10px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.72rem;
  line-height: 1.2;
}

.today-cash-table th {
  background: #f1f5f9;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.col-sn,
.col-income {
  position: sticky !important;
  z-index: 10;
  background-color: white !important;
}

.col-sn {
  left: 0;
  width: 2.25rem;
  min-width: 2.25rem;
  text-align: center;
}

.col-income {
  left: 2.25rem;
  min-width: 6rem;
  text-align: left !important;
  white-space: nowrap;
}

.today-cash-table thead th.col-sn,
.today-cash-table thead th.col-income {
  z-index: 30;
  background-color: #f1f5f9 !important;
}

.today-cash-table thead th.col-sn { left: 0; }
.today-cash-table thead th.col-income { left: 2.25rem; }

.today-cash-table tr:hover td {
  background-color: #f8fafc;
}

.today-cash-table tr:hover td.col-sn,
.today-cash-table tr:hover td.col-income {
  background-color: #f8fafc !important;
}

.today-cash-table th.num,
.today-cash-table td.num {
  width: 5.5rem;
  min-width: 5.5rem;
  text-align: center;
  white-space: nowrap;
}

.today-cash-table th.total-col,
.today-cash-table td.total-cell {
  background-color: #f8fafc;
}

.today-cash-table .col-sys-hidden {
  display: none;
}

.today-cash-table tr:hover td.total-cell {
  background-color: #f1f5f9 !important;
}

.total-cell-wrap {
  position: relative;
  display: inline-block;
  cursor: help;
}

.total-cell-value {
  font-weight: 600;
  color: #0d4169;
  border-bottom: 1px dotted #94a3b8;
}

.total-cell-value.vat-total-value {
  font-weight: 700;
  color: #334155;
}

.today-cash-table td.variation-cell {
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.total-cell-dropdown {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  min-width: 13rem;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  text-align: left;
  white-space: nowrap;
}

.total-cell-wrap:hover .total-cell-dropdown {
  display: block;
}

.pinned-breakdown-wrap:hover .total-cell-dropdown {
  display: block;
}

.total-cell-dropdown-title {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e2e8f0;
}

.total-cell-dropdown-line {
  font-size: 0.68rem;
  color: #334155;
  line-height: 1.5;
}

.total-cell-dropdown-line + .total-cell-dropdown-line {
  margin-top: 4px;
}

.total-cell-tag {
  display: inline-block;
  min-width: 3.25rem;
  margin-right: 4px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #94a3b8;
}

.withdrawal-detail-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.72rem;
  color: #1e293b;
  border-collapse: collapse;
}

.withdrawal-detail-table th,
.withdrawal-detail-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}

.withdrawal-detail-table th.num,
.withdrawal-detail-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.withdrawal-detail-table thead th {
  background: #f1f5f9;
  font-weight: 600;
}

.withdrawal-amount-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 0.35rem 1rem;
}

.withdrawal-amount-row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  white-space: nowrap;
  justify-self: start;
  width: auto;
  max-width: 100%;
}

.withdrawal-amount-label {
  font-weight: 600;
  color: #64748b;
}

.withdrawal-amount-value {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #1e293b;
}

.withdrawal-amount-row--grand .withdrawal-amount-value {
  color: #0d4169;
}

.withdrawal-amount-row--payable {
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
}

.withdrawal-amount-row--payable .withdrawal-amount-value {
  color: #0d4169;
  font-weight: 700;
}

@media (min-width: 640px) {
  .withdrawal-amount-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .withdrawal-amount-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
</style>
